import { Flex, Text } from '@chakra-ui/react'
import { GameRules } from 'types/types'
import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown/CurrencyDropdown'
import { AmountButtons } from '@/components/atoms/AmountButtons/AmountButtons'
import { currenciesSettings, DoubleState, useDoubleStore } from '@/context/DoubleProvider'
import { StyledInput2 } from '@/components/atoms/StyledInput/StyledInput'
import { getTokenByDenom } from '@/services/token-service'
import { useEffect } from 'react'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { useBalanceStore } from '@/context/UserBalance'
import { useShallow } from 'zustand/react/shallow'
import { useTranslation } from 'next-i18next/pages'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'

export const DoubleForm = () => {
  const denom = useBalanceStore((state) => state.denom)
  const { t } = useTranslation()

  useEffect(() => {
    useDoubleStore.getState().setDenom(denom)
  }, [denom])

  return (
    <Flex
      rounded={'20px'}
      flexDir={{ base: 'column', lg: 'row' }}
      bg={'ninja.600'}
      gap={{ base: 2, lg: 6 }}
      p={4}
      mb={{ base: 4, lg: 2 }}
      alignItems={{ base: 'space-between', lg: 'center' }}
    >
      <Flex width={{ base: 'full', lg: 'auto' }} flexShrink={0} justifyContent={'space-between'}>
        <Flex minW={'140px'} h={{ base: 'auto', lg: '70px' }} flexDir={'column'} justifyContent={'space-between'}>
          <Text fontWeight={700}>{t('common.your-bet')}</Text>
          <ObservableCurrencyDropdown />
          <Flex mt={2} ml={1} hideBelow="lg">
            <GameInfo rtp="95%" />
          </Flex>
        </Flex>
        <RulesButton game={GameRules.DOUBLE} styles={{ display: { base: 'block', lg: 'none' } }} />
      </Flex>
      <DoubleStyledInput />
      <DoubleAmountButtons />
      <RulesButton game={GameRules.DOUBLE} styles={{ display: { base: 'none', lg: 'block' } }} />
      <Flex hideFrom="lg">
        <GameInfo rtp="95%" />
      </Flex>
    </Flex>
  )
}

const DoubleStyledInput = () => {
  const amount = useDoubleStore((state) => state.amount)
  const errors = useDoubleStore((state) => state.errors)
  const isDirty = useDoubleStore((state) => state.isDirty)
  const denom = useDoubleStore((state) => state.denom)
  const setValues = useDoubleStore((state) => state.setValues)
  const maxValue = useDoubleStore((state) => state.maxValue)
  const { t } = useTranslation()
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)

  const token = getTokenByDenom(denom)
  const isUsdc = token?.currency === SupportedCurrencies.USDC
  const maxInfoText =
    isUsdc && !isUsd
      ? t('common.max') + ' ' + usdcToFiat(parseFloat(String(maxValue))).toFixed(2) + ' ' + getSymbol()
      : t('common.max') + ' ' + maxValue + ' ' + token.name

  return (
    <StyledInput2
      title={t('common.amount')}
      placeholder={t('common.enter-amount')}
      icon={token.icon('18px')}
      setValue={(amount) => setValues({ amount: { value: amount } })}
      errors={errors}
      isDirty={isDirty}
      amount={amount}
      clearable={true}
      infoText={maxInfoText}
      denom={denom}
    />
  )
}

const DoubleAmountButtons = () => {
  const denom = useDoubleStore((state) => state.denom)
  const amount = useDoubleStore((state) => state.amount)
  const setValues = useDoubleStore((state) => state.setValues)

  return (
    <Flex
      flexShrink={0}
      minW={{ base: 0, md: '360px' }}
      h={{ base: 'auto', lg: '61px' }}
      rounded={'xl'}
      alignItems={'center'}
      px={{ base: 0, lg: 4 }}
      border={{ base: 'none', lg: '1px solid' }}
      borderColor={{ base: 'none', lg: 'ninja.500' }}
      display={{ base: 'flex', lg: 'none', xl: 'flex' }}
    >
      <AmountButtons
        maxValue={currenciesSettings.maxValue[denom]}
        denom={denom}
        setAmount={(value) => setValues({ amount: { value } })}
        amount={amount}
      />
    </Flex>
  )
}

const ObservableCurrencyDropdown = () => {
  const isAllowedToBet = useDoubleStore(
    useShallow((state) => [DoubleState.INIT, DoubleState.PROCESSING].includes(state.state!))
  )

  useEffect(() => {
    if (!isAllowedToBet) {
      useBalanceStore.getState().lockBalance()
    }

    return () => {
      useBalanceStore.getState().unLockBalance(true)
    }
  }, [isAllowedToBet])

  return <CurrencyDropdown isDisabled={!isAllowedToBet} />
}
